import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiInformationOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { AssetName } from '../ArchivedMarker'
import type { ContractAsset } from '../../hooks/useQueries'

interface ContractAssetsClauseProps {
  org: string
  assets: ContractAsset[]
  /** The clause row grid, so the assets line up with the clause values. */
  rowClassName: string
}

/**
 * The contract's assets, read only, in the Clauses tab (spec 2026-10-27-05).
 * A lease's lots, an insurance's insured assets and a maintenance contract's
 * equipment are the assets attached to the contract, never a property: this
 * block shows them with links, and the info icon says where they are edited.
 */
export function ContractAssetsClause({ org, assets, rowClassName }: ContractAssetsClauseProps) {
  const { t } = useTranslation('contracts')
  const hint = t('clauses.assets_block.hint')
  return (
    <div className={rowClassName} data-testid="clauses-assets-block">
      <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <span>{t('clauses.assets_block.title')}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex text-muted-foreground hover:text-foreground"
              aria-label={hint}
              data-testid="clauses-assets-info"
            >
              <AppIcon path={mdiInformationOutline} size="xs" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs" data-testid="clauses-assets-info-tooltip">{hint}</TooltipContent>
        </Tooltip>
      </div>
      {assets.length === 0 ? (
        <p className="col-span-2 text-sm text-muted-foreground" data-testid="clauses-assets-empty">{t('clauses.assets_block.empty')}</p>
      ) : (
        <ul className="col-span-2 flex flex-wrap gap-x-3 gap-y-1 text-sm min-w-0">
          {assets.map(a => (
            <li key={a.uid} className="min-w-0" data-testid={`clauses-asset-${a.asset_uid}`}>
              <Link
                to="/$org/assets/$assetUid"
                params={{ org, assetUid: a.asset_uid }}
                className="text-primary hover:underline"
                data-testid={`clauses-asset-link-${a.asset_uid}`}
              >
                <AssetName name={a.asset?.name || t('detail.asset_unknown')} archivedAt={a.asset?.archived_at} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
